import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Printer, FileText, MessageCircle, Mail } from 'lucide-react';
import { printDocument, shareDocument, emailDocument } from '../lib/printDocument';
import { getCustomers } from '../api';
import { customerFullName } from '../lib/customerSearch';

const toWaPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('972')) return digits;
  return digits.startsWith('0') ? `972${digits.slice(1)}` : digits;
};

const findPhone = async (name) => {
  const n = (name || '').split(/\s+/).join(' ').trim().toLowerCase();
  if (!n) return '';
  const customers = await getCustomers().catch(() => []);
  return customers.find((c) => customerFullName(c).split(/\s+/).join(' ').toLowerCase() === n)?.phone || '';
};
import { toast } from '../hooks/use-toast';

// getDoc: () => printDocument payload; docRef: { type, recordId } for the running document number
export const PrintButtons = ({ getDoc, docRef, testIdPrefix = 'form', shareOnly = false }) => {
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const payload = () => ({ ...getDoc(), docRef });

  const print = (showCost) => {
    if (!printDocument({ ...payload(), showCost })) toast({ title: 'החלון נחסם', description: 'אפשר חלונות קופצים בדפדפן כדי להדפיס', variant: 'destructive' });
  };

  const sendWhatsapp = async () => {
    const w = window.open('', '_blank');
    try {
      const doc = payload();
      const [{ url, title }, phone] = await Promise.all([shareDocument(doc), findPhone(doc.customerName)]);
      const text = `שלום ${doc.customerName || ''},\nמצורף ${title}.\nלצפייה והורדה כ-PDF: ${url}`;
      const waPhone = toWaPhone(phone);
      const waUrl = `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`;
      if (!waPhone) toast({ title: 'לא נמצא טלפון', description: 'אין טלפון שמור בכרטיס הלקוח - בחר איש קשר בוואטסאפ' });
      if (w) w.location.href = waUrl; else window.location.href = waUrl;
    } catch {
      if (w) w.close();
      toast({ title: 'שגיאה', description: 'לא ניתן ליצור קישור למסמך', variant: 'destructive' });
    }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const doc = payload();
      const { token } = await shareDocument(doc);
      await emailDocument(token, email.trim(), doc.customerName);
      toast({ title: 'נשלח', description: `המסמך נשלח ל-${email.trim()}` });
      setEmailOpen(false);
    } catch (e) {
      toast({ title: 'שגיאה', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-print-buttons`}>
      {!shareOnly && (
        <>
          <Button type="button" variant="outline" onClick={() => print(true)} className="border-2" data-testid={`${testIdPrefix}-print-full-btn`}>
            <Printer className="w-4 h-4 ml-2" />הדפס מלא
          </Button>
          <Button type="button" variant="outline" onClick={() => print(false)} className="border-2 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`${testIdPrefix}-print-customer-btn`}>
            <FileText className="w-4 h-4 ml-2" />הדפס ללקוח (בלי מחיר עלות)
          </Button>
        </>
      )}
      <Button type="button" variant="outline" onClick={sendWhatsapp} className="border-2 border-green-400 text-green-700 hover:bg-green-50" data-testid={`${testIdPrefix}-whatsapp-btn`}>
        <MessageCircle className="w-4 h-4 ml-2" />שלח בוואטסאפ
      </Button>
      <Button type="button" variant="outline" onClick={() => setEmailOpen(true)} className="border-2 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`${testIdPrefix}-email-btn`}>
        <Mail className="w-4 h-4 ml-2" />שלח במייל
      </Button>
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent dir="rtl" className="max-w-md" data-testid={`${testIdPrefix}-email-dialog`}>
          <DialogHeader><DialogTitle>שליחת המסמך במייל</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">הלקוח יקבל את גרסת הלקוח (בלי מחיר עלות) וקישור להורדה כ-PDF.</p>
          <Input type="email" dir="ltr" placeholder="customer@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`${testIdPrefix}-email-input`} />
          <Button type="button" onClick={sendEmail} disabled={busy || !email.includes('@')} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`${testIdPrefix}-email-send-btn`}>
            {busy ? 'שולח...' : 'שלח'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
